import { useMemo } from "react";
import type { ModifierState } from "@/domain/profile-resolver/index";
import type { Category } from "@/domain/types";
import { ingestSC2 } from "@/games/sc2/sc2-ingest";
import { charMapOf, isBoardLayerMapping } from "@/hardware/keyboard-layouts/index";
import { activeLayout } from "@/hardware/keyboard-layouts/picker";
import { builtinEntries, builtins } from "@/hardware/keyboards/builtin";
import type { ImportedKeyboard } from "@/hardware/keyboards/keyboard-import";
import { deriveModRemapLayer } from "@/hardware/keyboards/mod-remap-layer";
import type { ActiveSetup } from "@/platform/catalog/active-setup";
import { renderText } from "@/platform/catalog/download";
import { DEFAULT_BOARD_ID } from "./use-page-state";
import { useSideRender } from "./use-side-render";

/**
 * One render side's inputs, after the per-side CHOOSING is resolved by the page (a board-bound pin and
 * a share-link override on the A side; "Same as A" on the compare B side). useSide owns everything
 * downstream of that choice - the lens (board, layout, Plus remap) and the Software (the character-
 * space Profile) - so the A side and the compare B side derive identically instead of as two
 * hand-synced copies. The page passes each side its resolved choices; useSide does the rest.
 */
export interface SideInput {
  /** The resolved software choice for this side (an SC2 source or a simple-Game Profile). */
  setup: ActiveSetup;
  /** The effective board id this side renders on (pin / override / "Same as A" already resolved). */
  boardId: string;
  /** The layer/layout selection id (pin / override / persisted / "Same as A" resolved); undefined = default. */
  layoutId: string | undefined;
  /** Session-imported boards, by id. */
  imported: Record<string, ImportedKeyboard>;
  /** The Context id the projector resolves against. */
  contextId: string;
  /** Held / latched modifier state. */
  mods: ModifierState;
  categories: Record<string, Category>;
  rapidFireOn: boolean;
  hideDefaults: boolean;
  /** Whether this side renders (the A side is always on; the B side only while comparing). */
  enabled: boolean;
}

/**
 * Resolve one render side end to end: the lens (Keyboard + Keyboard Layout, with a TheCore Plus
 * modifier-remap board layer where the source carries one) and the Software (the character-
 * space Profile - SC2 ingests its source text, a simple Game uses its hand-authored Profile), then
 * drive the shared render pipeline (parse / project / compose) via useSideRender. Returns
 * the lens pieces the page still needs (entry, board, keyboardLayout, plusLayer, activeText) plus the
 * full render output (table, layers, keycaps, zones, ...).
 */
export function useSide({
  setup,
  boardId,
  layoutId,
  imported,
  contextId,
  mods,
  categories,
  rapidFireOn,
  hideDefaults,
  enabled,
}: SideInput) {
  const source = setup.kind === "sc2" ? setup.source : null;
  const simpleProfile = setup.kind === "profile" ? setup.profile : null;
  const preset = setup.kind === "sc2" ? setup.preset : null;
  // a built-in board can carry board layers too (VIA-sourced); a session import never
  // collides because the pipeline ids boards imported-N. Fall back to the default board so an
  // unresolvable id never wedges the page (the page's effect resyncs the id). Own-key lookups
  // only: these are plain records, so a hostile id naming an Object.prototype member
  // ("constructor", from a tampered blob or hand-edited link) must resolve as unknown, not as
  // an inherited function that crashes the lens chain.
  const entry = Object.hasOwn(imported, boardId)
    ? imported[boardId]
    : Object.hasOwn(builtinEntries, boardId)
      ? builtinEntries[boardId]
      : undefined;
  const board =
    entry?.keyboard ??
    (Object.hasOwn(builtins, boardId) ? builtins[boardId] : undefined) ??
    builtins[DEFAULT_BOARD_ID];
  const keyboardLayout = activeLayout(entry, layoutId);
  // The layout the Preset download serializes through: the active mapping as a char table. For a
  // universal Keyboard Layout charMapOf is identity (tokens and overflow ride along); a board
  // layer yields its synthesized charmap. keyboardLayout here is never the derived Plus layer
  // (that is renderLayout below), so a Plus source serializes through its UNDERLYING universal
  // layout, exactly as its download always has. Computed here - not at the page - so the
  // download button and the rendered board read the SAME layout and cannot drift.
  const downloadLayout = useMemo(
    () => (board ? charMapOf(board, keyboardLayout) : null),
    [board, keyboardLayout],
  );
  // The text this side renders: a published Preset renders the EXACT file its Download serves
  // (servedText - the vendored canonical seed, or the conversion-engine output for the active
  // lens; decision 0022 amendment), so the board depicts what the player installs. Every other
  // SC2 source (a stock table, a pinned board-bound source, an upload) has no generated
  // download - its own text IS the artifact - and keeps the character-space render.
  const activeText = useMemo(
    () => (source?.text == null ? null : renderText(preset, source.text, downloadLayout)),
    [source?.text, preset, downloadLayout],
  );
  // the active character-space Profile: SC2 ingests the served text (its tokens re-projected per
  // lens switch), filling gaps from the Blizzard Standard table - the fill deliberately stays
  // character-space, because in game an unbound Command uses the game's own default, which
  // follows the player's layout. A simple Game uses its Profile directly. The pin rides the
  // source so a board-bound source keeps its pinned lens.
  const renderProfile = useMemo(
    () =>
      setup.kind === "sc2"
        ? activeText != null
          ? ingestSC2(activeText, source?.pin, { fillDefaults: true })
          : null
        : simpleProfile,
    [setup.kind, activeText, source?.pin, simpleProfile],
  );
  // TheCore Plus: a source carrying the derived-lens marker renders through a synthetic
  // modifier-remap board layer built from this side's OWN board + hand. Null when the board has no key
  // beside Control (Plus-incompatible) or the active mapping is already a board layer.
  const modRemap = source?.modRemap;
  const plusLayer = useMemo(
    () =>
      modRemap && board && !isBoardLayerMapping(keyboardLayout)
        ? deriveModRemapLayer(board, keyboardLayout, modRemap.hand)
        : null,
    [modRemap, board, keyboardLayout],
  );
  // why the Plus remap is not rendering, when it is not: the remap rides a universal Keyboard
  // Layout's character map, so a board layer as the active mapping can never host it, and a
  // board with no key beside this hand's Control has nowhere to put the relocated Shift. Named
  // (not just boolean) so the annotation states the real cause - the gate policy is to render
  // unremapped and say why, never silently mis-label.
  const plusUnavailable: "board-layer" | "no-neighbor" | null = modRemap
    ? isBoardLayerMapping(keyboardLayout)
      ? "board-layer"
      : plusLayer == null
        ? "no-neighbor"
        : null
    : null;
  const renderLayout = plusLayer ?? keyboardLayout;
  const render = useSideRender({
    renderProfile,
    board,
    keyboardLayout: renderLayout,
    activeText,
    contextId,
    mods,
    categories,
    rapidFireOn,
    hideDefaults,
    enabled,
  });
  return {
    entry,
    board,
    keyboardLayout,
    plusLayer,
    plusUnavailable,
    activeText,
    downloadLayout,
    ...render,
  };
}
